import { useMemo } from "react";
import { SlideUpModal } from "src/components/SlideUpModal";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import { useWebClientApi } from "src/api";
import {
  toCreateIncomeReq,
  toUpdateIncomeReq,
  type IncomeFormData,
} from "src/api/adapters/income";
import {
  notEmpty,
  type ValidationMapperFunc,
  test,
  validate,
  commaSeparatedDigits,
  greaterThanMinAmount,
  validAges,
} from "src/utils";
import {
  AddMoneyPlusIcon,
  AdditionalIncomeFields,
  FormTable,
  FormTableBody,
} from "@porchpass/ui";
import { DomainIncomeType } from "@porchpass/api-client";
import {
  additionalIncomeErrorsAfterSubmit,
  fromUiAdditionalIncomePatch,
  toUiAdditionalIncome,
} from "src/forms/adapters/additionalIncome";

const validationMapper: ValidationMapperFunc<IncomeFormData> = (_) => ({
  amount: [
    test(notEmpty, "Must enter an amount"),
    test(greaterThanMinAmount(0), "Must be greater than 0"),
  ],
  children_ages: [
    test(
      commaSeparatedDigits,
      "Children's ages must be a comma separated list of numbers",
    ),
    test(validAges, "Children's ages must be a valid age"),
  ],
});

type ChildSupportModalProps = {
  incomeItem: Partial<IncomeFormData>;
  closeHandler: () => void;
  confirmHandler: () => void;
};

export default ({
  incomeItem,
  closeHandler,
  confirmHandler,
}: ChildSupportModalProps) => {
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<IncomeFormData>(
    { ...incomeItem, income_type: "CHILD_SUPPORT" },
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );

  const [submitHandler, loading, , hasAttemptedFormSubmission] =
    useDlaFormSubmit(
      async (data: IncomeFormData) => {
        if (incomeItem.id) {
          return api.updateIncome(incomeItem.id, toUpdateIncomeReq(data));
        }
        return api.createIncome(toCreateIncomeReq(data));
      },
      { onSuccess: () => confirmHandler() },
    );

  const formErrors = validate<IncomeFormData>(formData, validationMapper);

  const incomeValue = useMemo(() => {
    const ui = toUiAdditionalIncome(formData);
    ui.type = DomainIncomeType.IncomeChildSupport;
    return ui;
  }, [formData]);

  const incomeErrors = useMemo(
    () =>
      additionalIncomeErrorsAfterSubmit(formErrors, hasAttemptedFormSubmission),
    [formErrors, hasAttemptedFormSubmission],
  );

  const applyIncomeChange = (patch: Partial<typeof incomeValue>) => {
    const mapped = fromUiAdditionalIncomePatch(patch);
    for (const [key, val] of Object.entries(mapped)) {
      updateField(key as keyof IncomeFormData)(val);
    }
  };

  return (
    <SlideUpModal
      title="Child Support"
      isOpen={true}
      closeHandler={closeHandler}
      actionButtonText="Add Income"
      actionButtonClickHandler={() => void submitHandler(formData, formErrors)}
      isLoading={loading}
    >
      <FormTable
        title="Income Details"
        headerIcon={
          <AddMoneyPlusIcon
            variant="bulk-rounded"
            size={16}
            className="text-[#BC3C7C]"
          />
        }
      >
        <FormTableBody>
          <AdditionalIncomeFields
            income={incomeValue}
            variant="childSupport"
            errors={incomeErrors}
            onFieldChange={(field, value) => {
              applyIncomeChange({ [field]: value });
            }}
            onBulkChange={applyIncomeChange}
          />
        </FormTableBody>
      </FormTable>
    </SlideUpModal>
  );
};
